import React, { useMemo } from 'react'
import GradeIcon from '@Components/Cards/ClassroomCard/Grade.icon'
import { Color } from '@eduact/student-theme'
import { LayoutProps, SpaceProps } from 'styled-system'
import { useNavigate, generatePath } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import routes from '@Constants/Routes'
import { useAppSelector } from '@Hooks/ReduxHooks'
import {
	StyledCourseCard,
	StyledCourseImage,
	Badge,
	CardBody,
	Title,
	Instructor,
	InfoWrapper,
	GradeWrapper,
	Grade,
	SubjectWrapper,
	SubjectImage,
	Subject,
	CardButton,
	PriceRibbon,
	OldPrice,
	BundleChipsRow,
	BundleChip,
} from './CourseCard.styled'

export type CourseCardProps = {
	course: StandaloneCourse
	onClick?: React.MouseEventHandler
	// bundle context: a member course that is not separately purchasable is
	// shown but marked unavailable and is not click-through-able
	unavailable?: boolean
} & LayoutProps &
	SpaceProps

const CourseCard: React.FC<CourseCardProps> = ({ course, onClick, unavailable, ...props }) => {
	const navigate = useNavigate()
	const [t] = useTranslation('courses')
	const [tBundles] = useTranslation('bundles')
	const [tEdu] = useTranslation('educational_info')
	const bundlesEnabled = useAppSelector(s => s.settings.bundles_enabled)

	const owned = Boolean(course.owned)
	const expired = Boolean(course.enrollment?.expired)
	const isFree = Number(course.price) === 0

	const grade = useMemo(() => course.educationYears?.[0]?.name, [course])

	const buttonLabel = useMemo(() => {
		if (unavailable) return t('unavailable')
		if (owned) return expired ? t('expired') : t('continue')
		if (isFree) return t('free')
		return t('buy_now')
	}, [unavailable, owned, expired, isFree, t])

	const buttonColor = useMemo((): Color => {
		if (unavailable) return 'silver'
		if (owned) return expired ? 'silver' : 'purple'
		return 'primary'
	}, [unavailable, owned, expired])

	return (
		<StyledCourseCard
			onClick={e => {
				if (!course.accessible || unavailable) return
				if (!onClick) {
					e.stopPropagation()
					navigate(generatePath(routes.standaloneCourse, { label: course.label }))
				} else {
					onClick(e)
				}
			}}
			{...props}
		>
			<StyledCourseImage src={course.thumbnail ?? ''} />
			<Badge>{t('title')}</Badge>
			{!owned && (
				<PriceRibbon>
					{isFree ? t('free') : `${course.price} EGP`}
					{!isFree && course.old_price ? <OldPrice>{course.old_price} EGP</OldPrice> : null}
				</PriceRibbon>
			)}
			<CardBody>
				<Title>{course.title}</Title>
				<Instructor>
					{course.instructor?.user?.first_name} {course.instructor?.user?.last_name}
				</Instructor>
				<InfoWrapper>
					{grade && (
						<GradeWrapper>
							<GradeIcon />
							<Grade>{tEdu(`academic_year.${grade}`)}</Grade>
						</GradeWrapper>
					)}
					<SubjectWrapper>
						<SubjectImage src={course.category?.icon ?? ''} />
						<Subject>{course.category?.name}</Subject>
					</SubjectWrapper>
				</InfoWrapper>
				{bundlesEnabled && course.bundles && course.bundles.length > 0 && (
					<BundleChipsRow>
						{course.bundles.map(bundleRef => (
							<BundleChip
								key={`bundle-chip-${bundleRef.id}`}
								onClick={e => {
									e.stopPropagation()
									navigate(generatePath(routes.bundle, { label: bundleRef.label }))
								}}
							>
								{tBundles('part_of_bundle')}: {bundleRef.title}
							</BundleChip>
						))}
					</BundleChipsRow>
				)}
			</CardBody>
			<CardButton variant={buttonColor}>{buttonLabel}</CardButton>
		</StyledCourseCard>
	)
}

export default CourseCard
